// src/pages/ChatBot/configs/ConfigsPanel.tsx
//
// Configs tab of the Manage Chat console — the real, registry-driven tenant
// config admin surface (replacing the stand-in ChatbotConfigsPanel). On mount
// it GETs the config surface and renders a settings-style layout: a left
// sidebar that groups the RAG pipeline into an expandable two-level tree
// (parent sections → children), and the selected child's settings as
// label + help + control rows on the right.
//
// The tree is a PRESENTATION manifest only (sections.ts) — the backend registry,
// stages, and config ids are unchanged. A config id may appear under more than
// one child; because staged edits are keyed by config id, every instance stays
// in sync automatically. Surface entries the manifest doesn't name are appended
// to a per-stage fallback child so future registry defs still render.
//
// Editing is staged, not live: every change lands in `pending` (a set or a
// reset) and is committed all-at-once via the bottom Save bar. Rebuild-class
// (ingress) changes route through a confirm dialog first, since saving them
// queues a re-process of the workspace's documents. Navigating away while dirty
// is guarded — internally for the child nav, and via a props contract for the
// ManageChat top-tab switch (registerLeaveActions / onDirtyChange).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { ConfigApiError, fetchConfigSurface, saveConfigOverrides } from './api';
import type {
  ConfigSurface,
  ConfigSurfaceEntry,
  RebuildState,
  RejectedOverride,
} from './types';
import { RebuildConfirmModal, UnsavedChangesModal } from './config-modals';
import type { PendingMap } from './config-values';
import {
  displayValue,
  isInvalidPending,
  isInvalidValue,
  valuesEqual,
} from './config-values';
import { ConfigRow, RerankToggleRow } from './config-rows';
import { RebuildBanner } from './RebuildBanner';
import {
  CONFIG_SECTIONS,
  FALLBACK_CHILD_BY_STAGE,
  FUSION_ADAPTIVE_TAG,
  FUSION_BALANCE_ID,
  FUSION_ID,
  RERANKER_ID,
  RERANKER_OFF_TAG,
  RERANKER_ON_FALLBACK_TAG,
  REFERENCED_IDS,
  findTheme,
} from './sections';
import './ConfigsPanel.css';

// The theme the sidebar lands on first, before the surface loads.
const DEFAULT_CHILD_KEY = CONFIG_SECTIONS[0].key;

const SAVED_NOTICE_MS = 2500;

// Cadence for polling the config endpoint while a document re-process is in
// flight, so the rebuild banner tracks its progress live.
const REBUILD_POLL_MS = 4000;

// Contract with ManageChat, which owns the top-tab switch it cannot see from
// here: it reads dirtiness and drives the save/discard flow through registered,
// stable wrappers. `save` resolves true only when the save fully succeeded.
interface ConfigsPanelProps {
  onDirtyChange?: (dirty: boolean) => void;
  registerLeaveActions?: (actions: {
    save: () => Promise<boolean>;
    discard: () => void;
  }) => void;
}

// ── Panel ───────────────────────────────────────────────────────────────

export default function ConfigsPanel({
  onDirtyChange,
  registerLeaveActions,
}: ConfigsPanelProps) {
  const { isAuthenticated } = useAuth();

  const [surface, setSurface] = useState<ConfigSurface | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // The rebuild banner is tracked SEPARATELY from `surface`: while a re-process
  // is queued/running we poll the config endpoint, but we only ever push the
  // fresh `rebuild` slice into this state — never the surface's config entries —
  // so an in-flight poll can't overwrite the admin's pending edits. The banner
  // reads from here; `surface.rebuild` merely seeds it (via the sync effect).
  const [rebuild, setRebuild] = useState<RebuildState | undefined>(undefined);
  // Local-only dismissal of the "completed" success banner, keyed by the version
  // it announced so a fresh rebuild's completion shows again.
  const [dismissedRebuildVersion, setDismissedRebuildVersion] = useState<number | null>(null);

  // Selection state is a theme key from the manifest.
  const [activeChild, setActiveChild] = useState<string>(DEFAULT_CHILD_KEY);
  // Which themes have their "Advanced settings" block expanded — default all
  // collapsed (an empty set), so a pane opens on its simple settings only.
  const [expandedAdvanced, setExpandedAdvanced] = useState<Set<string>>(() => new Set());
  const [pending, setPending] = useState<PendingMap>({});

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [rejectionBanner, setRejectionBanner] = useState('');
  const [rejectedById, setRejectedById] = useState<Record<string, RejectedOverride>>({});
  const [savedNotice, setSavedNotice] = useState(false);

  // Rebuild confirm gate. While its resolver is set, a save is awaiting the
  // admin's answer in the dialog.
  const [rebuildModalOpen, setRebuildModalOpen] = useState(false);
  const [rebuildLabels, setRebuildLabels] = useState<string[]>([]);
  const rebuildResolverRef = useRef<((ok: boolean) => void) | null>(null);

  // Internal child-nav leave guard: the child key the admin is trying to move to.
  const [navTarget, setNavTarget] = useState<string | null>(null);

  // ── Load surface (mount, retry) ──
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setLoadError('');
    fetchConfigSurface()
      .then((data) => {
        if (!cancelled) setSurface(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load configuration.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, reloadToken]);

  // Seed / re-seed the rebuild banner from the surface whenever it (re)loads or a
  // save refetches it. Between those, the poll effect below owns `rebuild`.
  useEffect(() => {
    setRebuild(surface?.rebuild);
  }, [surface]);

  // Poll the config endpoint while a rebuild is queued/running so the banner
  // tracks it live. This deliberately updates ONLY the rebuild slice (never
  // `surface`), so polling can never clobber the admin's pending edits; late
  // responses are dropped on cleanup / unmount.
  useEffect(() => {
    const status = rebuild?.status;
    if (status !== 'queued' && status !== 'running') return undefined;
    let cancelled = false;
    const tick = () => {
      fetchConfigSurface()
        .then((fresh) => {
          if (!cancelled) setRebuild(fresh.rebuild);
        })
        .catch(() => {
          // Transient poll error — keep polling; a hard failure resurfaces on
          // the next manual save/refetch.
        });
    };
    const interval = setInterval(tick, REBUILD_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [rebuild?.status]);

  const entries = surface?.configs ?? [];
  const entriesById = useMemo(() => {
    const map: Record<string, ConfigSurfaceEntry> = {};
    for (const entry of entries) map[entry.id] = entry;
    return map;
  }, [entries]);

  // Ordered config ids per theme, split into basic/advanced tiers: the manifest's
  // ids (skipping any the surface lacks), then any surface entry the manifest never
  // names, appended to its stage's fallback theme's ADVANCED tier so future
  // registry defs still render, tucked out of the way.
  const childIdsByKey = useMemo(() => {
    const map: Record<string, { basic: string[]; advanced: string[] }> = {};
    for (const theme of CONFIG_SECTIONS) {
      map[theme.key] = {
        basic: theme.basic.filter((id) => entriesById[id]),
        advanced: theme.advanced.filter((id) => entriesById[id]),
      };
    }
    for (const entry of entries) {
      if (REFERENCED_IDS.has(entry.id)) continue;
      const fallback = FALLBACK_CHILD_BY_STAGE[entry.stage];
      if (map[fallback]) map[fallback].advanced.push(entry.id);
    }
    return map;
  }, [entries, entriesById]);

  const childHasEntries = useCallback(
    (childKey: string) => {
      const tiers = childIdsByKey[childKey];
      return (tiers?.basic.length ?? 0) + (tiers?.advanced.length ?? 0) > 0;
    },
    [childIdsByKey],
  );

  // On first load, land on the first theme that actually has settings so admins
  // never open onto an empty pane. Only moves when the current theme is empty,
  // so it's inert after edits/refetches.
  useEffect(() => {
    if (!surface) return;
    setActiveChild((prev) => {
      if (childHasEntries(prev)) return prev;
      const firstNonEmpty = CONFIG_SECTIONS.find((theme) => childHasEntries(theme.key));
      return firstNonEmpty ? firstNonEmpty.key : prev;
    });
  }, [surface, childHasEntries]);

  const isDirty = Object.keys(pending).length > 0;
  const hasInvalid = Object.values(pending).some(isInvalidPending);

  // Report dirtiness up to ManageChat, and clear it if we unmount while dirty.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // beforeunload native guard — only while there are unsaved edits.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Auto-dismiss the transient "Saved" confirmation.
  useEffect(() => {
    if (!savedNotice) return undefined;
    const timer = setTimeout(() => setSavedNotice(false), SAVED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [savedNotice]);

  // ── Editing ──
  // Any edit invalidates the last rejection: stale flags shouldn't linger once
  // the admin starts changing values again.
  const clearRejections = useCallback(() => {
    setRejectionBanner('');
    setRejectedById((prev) => (Object.keys(prev).length ? {} : prev));
  }, []);

  const applyEdit = useCallback(
    (entry: ConfigSurfaceEntry, nextValue: unknown) => {
      clearRejections();
      setPending((prev) => {
        const next = { ...prev };
        // A valid value that lands exactly on the current resolved value is a
        // no-op — drop it so the entry stops being dirty (this also cancels a
        // staged reset). Invalid values are kept so the row can flag itself.
        if (!isInvalidValue(nextValue) && valuesEqual(nextValue, entry.value)) {
          delete next[entry.id];
        } else {
          next[entry.id] = { kind: 'set', value: nextValue };
        }
        return next;
      });
    },
    [clearRejections],
  );

  const stageReset = useCallback(
    (entry: ConfigSurfaceEntry) => {
      clearRejections();
      setPending((prev) => ({ ...prev, [entry.id]: { kind: 'reset' } }));
    },
    [clearRejections],
  );

  const unstageEdit = useCallback(
    (entry: ConfigSurfaceEntry) => {
      clearRejections();
      setPending((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
    },
    [clearRejections],
  );

  const discard = useCallback(() => {
    setPending({});
    setSaveError('');
    setRejectionBanner('');
    setRejectedById({});
  }, []);

  // ── Save ──
  // Split pending into the two arms the endpoint expects. Invalid values are
  // filtered defensively (Save is already disabled while any exist).
  const partitionPending = useCallback(() => {
    const overrides: Record<string, unknown> = {};
    const resetIds: string[] = [];
    for (const [id, edit] of Object.entries(pending)) {
      if (edit.kind === 'reset') resetIds.push(id);
      else if (!isInvalidValue(edit.value)) overrides[id] = edit.value;
    }
    return { overrides, resetIds };
  }, [pending]);

  // Labels of pending ids whose def re-processes documents (changeCost
  // 'rebuild') — both sets and resets count, either changes the ingress config.
  const pendingRebuildLabels = useCallback(() => {
    const labels: string[] = [];
    for (const id of Object.keys(pending)) {
      const entry = entriesById[id];
      if (entry && entry.changeCost === 'rebuild') labels.push(entry.label);
    }
    return labels;
  }, [pending, entriesById]);

  // Persist and refetch. Resolves true only on a clean 200. On 422 keeps the
  // edits and flags each offending control; on any other error shows a banner.
  const persist = useCallback(async (): Promise<boolean> => {
    const { overrides, resetIds } = partitionPending();
    setIsSaving(true);
    setSaveError('');
    setRejectionBanner('');
    setRejectedById({});
    try {
      await saveConfigOverrides(overrides, resetIds);
      // Refetch so the surface (and any queued-rebuild banner) reflects reality,
      // then clear the staged edits.
      const fresh = await fetchConfigSurface();
      setSurface(fresh);
      setPending({});
      setSavedNotice(true);
      return true;
    } catch (err) {
      if (err instanceof ConfigApiError && err.status === 422) {
        setRejectionBanner('Nothing was saved — fix the flagged settings.');
        const byId: Record<string, RejectedOverride> = {};
        for (const rej of err.rejected ?? []) byId[rej.id] = rej;
        setRejectedById(byId);
        // Reveal any advanced block that hides a flagged field, so its red flag
        // isn't lost behind a collapsed disclosure.
        setExpandedAdvanced((prev) => {
          const next = new Set(prev);
          for (const theme of CONFIG_SECTIONS) {
            if (theme.advanced.some((id) => byId[id])) next.add(theme.key);
          }
          return next;
        });
        // Funnel-order violations are cross-field (the flagged control may not even
        // be the one edited) — surface them in a plain browser prompt for now.
        const funnelMessages = (err.rejected ?? [])
          .filter((rej) => rej.reason === 'funnel-order' && rej.detail)
          .map((rej) => rej.detail as string);
        if (funnelMessages.length > 0) {
          window.alert(`Nothing was saved:\n\n${funnelMessages.join('\n')}`);
        }
      } else {
        setSaveError(err instanceof Error ? err.message : 'Failed to save configuration.');
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [partitionPending]);

  // The full save flow: rebuild-class edits open the confirm dialog first and
  // the save resolves through its answer; everything else persists straight.
  const runSave = useCallback(async (): Promise<boolean> => {
    if (!isDirty || isSaving || hasInvalid || rebuildModalOpen) return false;
    const rebuildAffected = pendingRebuildLabels();
    if (rebuildAffected.length > 0) {
      return new Promise<boolean>((resolve) => {
        rebuildResolverRef.current = resolve;
        setRebuildLabels(rebuildAffected);
        setRebuildModalOpen(true);
      });
    }
    return persist();
  }, [isDirty, isSaving, hasInvalid, rebuildModalOpen, pendingRebuildLabels, persist]);

  const confirmRebuild = useCallback(async () => {
    const ok = await persist();
    setRebuildModalOpen(false);
    const resolve = rebuildResolverRef.current;
    rebuildResolverRef.current = null;
    resolve?.(ok);
  }, [persist]);

  const cancelRebuild = useCallback(() => {
    setRebuildModalOpen(false);
    const resolve = rebuildResolverRef.current;
    rebuildResolverRef.current = null;
    // Cancelling resolves the save as not-done — the dirty state stays intact.
    resolve?.(false);
  }, []);

  // ── Leave-action contract for ManageChat ──
  // Register stable wrappers once; they always call the latest closures via
  // refs, so ManageChat's stored actions never go stale.
  const runSaveRef = useRef(runSave);
  const discardRef = useRef(discard);
  runSaveRef.current = runSave;
  discardRef.current = discard;

  const stableSave = useCallback(() => runSaveRef.current(), []);
  const stableDiscard = useCallback(() => discardRef.current(), []);

  useEffect(() => {
    registerLeaveActions?.({ save: stableSave, discard: stableDiscard });
  }, [registerLeaveActions, stableSave, stableDiscard]);

  // ── Child nav (internal leave guard) ──
  const handleChildNav = useCallback(
    (target: string) => {
      if (target === activeChild || isSaving) return; // ignore mid-save clicks
      if (isDirty) {
        setNavTarget(target); // open the unsaved-changes modal instead of moving
        return;
      }
      setActiveChild(target);
    },
    [activeChild, isDirty, isSaving],
  );

  // Expand/collapse of a theme's Advanced block is pure presentation — never
  // guarded, never touches edits (a staged edit inside a collapsed block stays
  // dirty and savable, just as a hidden strategy knob does).
  const toggleAdvanced = useCallback((themeKey: string) => {
    setExpandedAdvanced((prev) => {
      const next = new Set(prev);
      if (next.has(themeKey)) next.delete(themeKey);
      else next.add(themeKey);
      return next;
    });
  }, []);

  const navSave = useCallback(async () => {
    const target = navTarget;
    // Close the guard first so the rebuild confirm (also inside this panel) is
    // reachable — otherwise it would render underneath this modal.
    setNavTarget(null);
    const ok = await runSave();
    if (ok && target) setActiveChild(target);
  }, [navTarget, runSave]);

  const navDiscard = useCallback(() => {
    const target = navTarget;
    discard();
    if (target) setActiveChild(target);
    setNavTarget(null);
  }, [navTarget, discard]);

  // ── Render gates ──
  if (isAuthenticated === null) return null;
  if (!isAuthenticated) {
    return (
      <div className="cfg-empty">
        You must be logged in as an admin to manage chatbot configuration.
      </div>
    );
  }

  if (isLoading && !surface) {
    return (
      <div className="cfg-loading">
        <span className="cfg-spinner" />
        Loading configuration…
      </div>
    );
  }

  if (loadError && !surface) {
    return (
      <div className="cfg-error-card">
        <p className="cfg-error-text">{loadError}</p>
        <button
          type="button"
          className="cfg-btn cfg-btn-secondary"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          Retry
        </button>
      </div>
    );
  }

  const activeTheme = findTheme(activeChild) ?? CONFIG_SECTIONS[0];
  const activeTiers = childIdsByKey[activeChild] ?? { basic: [], advanced: [] };
  const toEntries = (ids: string[]) =>
    ids
      .map((id) => entriesById[id])
      .filter((entry): entry is ConfigSurfaceEntry => Boolean(entry));
  const basicEntries = toEntries(activeTiers.basic);
  const advancedEntries = toEntries(activeTiers.advanced);
  // Auto-open the Advanced block when there are no basic settings, so the pane is
  // never blank; otherwise honour the admin's expand state (default collapsed).
  const advancedOpen = expandedAdvanced.has(activeChild) || basicEntries.length === 0;
  const hasAnyEntries = basicEntries.length > 0 || advancedEntries.length > 0;
  const cancelDisabled = !isDirty || isSaving;
  const saveDisabled = !isDirty || isSaving || hasInvalid || rebuildModalOpen;

  // Reranking: the "Reranking enabled" toggle is presentation sugar over the
  // reranker variant id — on == resolved value !== 'none'. Toggling never sends a
  // new id; it just stages a reranker value ('none' off, the surface default — or
  // llm-judge if that is itself 'none' — on). It renders atop the Advanced block.
  const rerankerEntry = entriesById[RERANKER_ID];
  const showRerankToggle = Boolean(activeTheme.rerankToggle && rerankerEntry);
  const rerankerDisplay = rerankerEntry
    ? displayValue(rerankerEntry, pending[RERANKER_ID])
    : undefined;
  const rerankOn = rerankerDisplay !== RERANKER_OFF_TAG;
  const handleRerankToggle = (nextOn: boolean) => {
    if (!rerankerEntry) return;
    if (!nextOn) {
      applyEdit(rerankerEntry, RERANKER_OFF_TAG);
      return;
    }
    const onTag =
      rerankerEntry.default !== RERANKER_OFF_TAG
        ? rerankerEntry.default
        : RERANKER_ON_FALLBACK_TAG;
    applyEdit(rerankerEntry, onTag);
  };

  // The keyword↔meaning balance slider is runtime-ignored while the fusion
  // strategy's EFFECTIVE value (staged edit if any) is the adaptive balance —
  // grey it so admins aren't tuning a dead control.
  const fusionEntry = entriesById[FUSION_ID];
  const fusionDisplay = fusionEntry ? displayValue(fusionEntry, pending[FUSION_ID]) : undefined;
  const balanceIgnored = fusionDisplay === FUSION_ADAPTIVE_TAG;

  // One row renderer shared by both tiers, so basic and advanced settings render
  // identically. Nested strategy knobs and the reranker/balance greying read their
  // parent entries GLOBALLY (via entriesById), so a control in one tier can govern
  // a row in another (e.g. fusion in advanced greys the basic balance slider).
  const renderRow = (entry: ConfigSurfaceEntry) => {
    // Nested strategy knob: render only while its parent variant's EFFECTIVE value
    // (staged edit if any, else the resolved value) equals its tag. A hidden row
    // keeps any staged edit in `pending` — it stays savable and reappears if the
    // strategy is switched back, so switching selects never drops edits.
    if (entry.parent) {
      const parentEntry = entriesById[entry.parent.id];
      const parentValue = parentEntry
        ? displayValue(parentEntry, pending[parentEntry.id])
        : undefined;
      if (parentValue !== entry.parent.tag) return null;
    }
    const isRerankerVariant = showRerankToggle && entry.id === RERANKER_ID;
    return (
      <ConfigRow
        key={entry.id}
        entry={entry}
        edit={pending[entry.id]}
        rejected={rejectedById[entry.id]}
        disabled={isSaving}
        nested={Boolean(entry.parent)}
        extraDisabled={
          (isRerankerVariant && !rerankOn) ||
          (entry.id === FUSION_BALANCE_ID && balanceIgnored)
        }
        hideVariantTags={isRerankerVariant ? [RERANKER_OFF_TAG] : undefined}
        onEdit={applyEdit}
        onReset={stageReset}
        onUnstage={unstageEdit}
      />
    );
  };

  return (
    <div className="cfg-panel">
      {rebuild &&
        !(rebuild.status === 'completed' && dismissedRebuildVersion === rebuild.toVersion) && (
          <RebuildBanner
            rebuild={rebuild}
            onDismiss={() => setDismissedRebuildVersion(rebuild.toVersion)}
          />
        )}
      {loadError && <div className="cfg-error-banner">{loadError}</div>}
      {saveError && <div className="cfg-error-banner">{saveError}</div>}
      {rejectionBanner && <div className="cfg-warn-banner">{rejectionBanner}</div>}

      <div className="cfg-body">
        {/* Sidebar — one button per outcome theme */}
        <nav className="cfg-nav" aria-label="Settings sections">
          {CONFIG_SECTIONS.map((theme) => (
            <button
              key={theme.key}
              type="button"
              className={`cfg-nav-item${activeChild === theme.key ? ' cfg-nav-active' : ''}`}
              onClick={() => handleChildNav(theme.key)}
            >
              {theme.label}
            </button>
          ))}
        </nav>

        {/* Selected theme's settings: basic first, then an Advanced disclosure */}
        <div className="cfg-detail">
          <h3 className="cfg-detail-title">{activeTheme.label}</h3>
          <p className="cfg-detail-blurb">{activeTheme.blurb}</p>

          {!hasAnyEntries ? (
            <p className="cfg-empty-note">No configurable settings in this section yet.</p>
          ) : (
            <>
              {basicEntries.length > 0 && (
                <div className="cfg-rows">{basicEntries.map(renderRow)}</div>
              )}

              {advancedEntries.length > 0 && (
                <div className="cfg-advanced">
                  <button
                    type="button"
                    className="cfg-advanced-toggle"
                    aria-expanded={advancedOpen}
                    onClick={() => toggleAdvanced(activeChild)}
                  >
                    <span
                      className={`cfg-nav-chevron${advancedOpen ? ' cfg-nav-chevron-open' : ''}`}
                    >
                      ▸
                    </span>
                    Advanced settings
                  </button>
                  {advancedOpen && (
                    <div className="cfg-advanced-body cfg-rows">
                      {showRerankToggle && rerankerEntry && (
                        <RerankToggleRow
                          on={rerankOn}
                          disabled={isSaving || rerankerEntry.locked || !rerankerEntry.enabled}
                          onToggle={handleRerankToggle}
                        />
                      )}
                      {advancedEntries.map(renderRow)}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer action bar — identical across every stage, pinned bottom-right */}
      <div className="cfg-footer">
        {savedNotice && <span className="cfg-saved-notice">Saved</span>}
        {isDirty && (
          <button
            type="button"
            className="cfg-btn cfg-btn-ghost"
            onClick={discard}
            disabled={cancelDisabled}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          className="cfg-btn cfg-btn-primary"
          onClick={() => {
            void runSave();
          }}
          disabled={saveDisabled}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Internal stage-nav guard */}
      <UnsavedChangesModal
        open={navTarget !== null}
        isSaving={isSaving}
        onSave={navSave}
        onDiscard={navDiscard}
        onKeepEditing={() => setNavTarget(null)}
      />

      {/* Rebuild confirm — shown on top of any guard, which closes first */}
      <RebuildConfirmModal
        open={rebuildModalOpen}
        affectedLabels={rebuildLabels}
        isSaving={isSaving}
        onConfirm={confirmRebuild}
        onCancel={cancelRebuild}
      />
    </div>
  );
}
